import { useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { usePwaEnabled } from '@/hooks/usePwaEnabled';

/**
 * Inner component that registers the SW with autoUpdate mode.
 * With autoUpdate, the new SW activates automatically on next navigation.
 * No user-facing UI is needed - updates happen seamlessly in background.
 */
const PWAUpdatePromptInner = () => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  
  // Register SW - with autoUpdate mode, needRefresh is never triggered
  useRegisterSW({
    onRegistered(r) {
      if (r && import.meta.env.PROD) {
        console.log('[PWA] SW Registered successfully');
        // Check for updates every hour in production
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
        intervalRef.current = setInterval(() => {
          r.update();
        }, 60 * 60 * 1000);
      }
    },
    onRegisterError(error) {
      console.error('[PWA] SW registration failed:', error);
    },
    onOfflineReady() {
      console.log('[PWA] App ready to work offline');
    },
  });

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // No UI needed - autoUpdate handles everything automatically
  return null;
};

/**
 * Gate component that prevents SW registration when PWA is disabled.
 */
export const PWAUpdatePrompt = () => {
  const pwaEnabled = usePwaEnabled();
  
  if (!pwaEnabled) {
    console.log('[PWA] Kill-switch active, skipping SW registration');
    return null;
  }
  
  return <PWAUpdatePromptInner />;
};
